import { useCallback, useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { formatDay } from '@/lib/format'
import type { AdminFetch } from './AdminCustomers'

type Courtesy = { id: string; quantity: number; includedUsers: number; validFrom: string; validUntil: string; lastDay: string; note: string; grantedBy: string; endedBy?: string | null; active: boolean }
type Overview = { courtesies: Courtesy[] }

function branches(quantity: number) {
  return `${quantity.toLocaleString('es-MX')} ${quantity === 1 ? 'sucursal' : 'sucursales'}`
}

/**
 * Branches a cloud tenant was given at no cost by a commercial agreement, until a last day. No new ones are given;
 * those on record join its branch limit while they and its plan are in force, and the admin can end one early.
 * Nothing shows for a tenant that never had one.
 */
export function AdminCourtesies({ environmentId, businessName, adminFetch, describeError, onChanged }: {
  environmentId: string; businessName: string; adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string; onChanged: () => void
}) {
  const id = useId()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [ending, setEnding] = useState<Courtesy | null>(null)
  const [endingBusy, setEndingBusy] = useState(false)
  const [endError, setEndError] = useState('')
  const [notice, setNotice] = useState('')
  const path = `/admin/environments/${environmentId}/branch-courtesies`

  const load = useCallback(async () => {
    setLoading(true); setLoadError('')
    try { setOverview(await adminFetch<Overview>(path)) } catch (cause) { setLoadError(describeError(cause, 'No pudimos leer las cortesías.')) } finally { setLoading(false) }
  }, [adminFetch, describeError, path])

  useEffect(() => { void load() }, [load])

  async function end() {
    if (!ending) return
    setEndingBusy(true); setEndError('')
    try {
      const updated = await adminFetch<Overview>(`${path}/${ending.id}/end`, { method: 'POST' })
      setOverview(updated)
      setNotice(`Cortesía terminada: ${branches(ending.quantity)} dejan de contar.`)
      setEnding(null)
      onChanged()
    } catch (cause) { setEndError(describeError(cause, 'No pudimos terminar la cortesía.')) } finally { setEndingBusy(false) }
  }

  const courtesies = overview?.courtesies ?? []
  if (overview && courtesies.length === 0) return null
  return <section className="admin-customer-block" aria-labelledby={`${id}-heading`}>
    <h4 id={`${id}-heading`}>Sucursales de cortesía</h4>
    <p className="field-hint">Dadas sin costo, por un acuerdo comercial. Suman al límite de sucursales hasta su último día, mientras el plan siga pagado. Ya no se dan nuevas.</p>
    {notice && <Notice tone="success">{notice}</Notice>}
    {loading && !overview ? <LoadingState label="Leyendo las cortesías…" />
      : loadError && !overview ? <ErrorState title={loadError} retry={() => void load()} busy={loading} />
      : <div className="price-table-wrap"><table>
        <thead><tr><th><span className="sr-only">Acciones</span></th><th>Sucursales</th><th>Usuarios por sucursal</th><th>Último día</th><th>Acuerdo</th><th>Registró</th><th>Estado</th></tr></thead>
        <tbody>{courtesies.map((courtesy) => <tr key={courtesy.id}>
          <td>{courtesy.active ? <Button emphasis="ghost" intent="danger" onClick={() => { setEndError(''); setNotice(''); setEnding(courtesy) }} aria-label={`Terminar la cortesía de ${branches(courtesy.quantity)}`}>Terminar</Button> : null}</td>
          <td>{courtesy.quantity.toLocaleString('es-MX')}</td>
          <td>{courtesy.includedUsers.toLocaleString('es-MX')}</td>
          <td>{formatDay(courtesy.lastDay)}</td>
          <td className="admin-courtesy-note">{courtesy.note}</td>
          <td>{courtesy.grantedBy}</td>
          <td><span className="status-chip" data-active={courtesy.active}>{courtesy.active ? 'Vigente' : courtesy.endedBy ? `Terminada por ${courtesy.endedBy}` : 'Terminada'}</span></td>
        </tr>)}</tbody>
      </table></div>}

    <ConfirmDialog open={ending !== null} title="Terminar la cortesía" action="Terminar cortesía" busy={endingBusy} error={endError || undefined}
      onCancel={() => { if (!endingBusy) setEnding(null) }} onConfirm={() => void end()}>
      {ending && <p>Las {branches(ending.quantity)} de cortesía de {businessName} dejan de contar ahora, antes de su último día, el {formatDay(ending.lastDay)}. Si tiene más sucursales activas que su límite, las más recientes no abren sesión.</p>}
    </ConfirmDialog>
  </section>
}
